/**
 * searchCriteria — the map-narrowing vocabulary of a Search (ADR-0001).
 *
 * One module owns the client-side narrowing shape (`MapFilters`), the pure
 * narrowing function (`filterApartments`), and the criteria-panel →
 * map-vocabulary translation (`mapFiltersFromCriteria`,
 * `normalizeCriteriaBedroom`). Historically the shape and helpers lived
 * inside the MapFilterOverlay COMPONENT, so a store (now the Search store's Lens)
 * imported from a component and the translation table was inlined in
 * apartmentStore.performSearch — no locality. The component is now purely
 * presentational, as its own docstring always claimed.
 */
import { normalizeApartment, normalizeBedKey, cheapestStartingPrice } from '../types/apartmentCanonical';
import type { FiltersState } from '../types/stores';

export interface MapFilters {
  minBudget: number | null;
  maxBudget: number | null;
  bedrooms: string[];
  buildingAges: string[];
}

export const EMPTY_MAP_FILTERS: MapFilters = {
  minBudget: null,
  maxBudget: null,
  bedrooms: [],
  buildingAges: [],
};

// Product default for the BROWSE map's filters: modern stock first (the
// locator's "age default <25yrs" rule). Seeded by the browse page as
// VISIBLE, removable chips rather than a hidden query-side constraint —
// silent constraint dropping/adding is the known trust-killer. The vibe
// map starts EMPTY: its results are conversation-curated, and a default
// chip hiding a building the AI deliberately showed would make the map
// contradict the chat.
export const DEFAULT_MAP_FILTERS: MapFilters = {
  ...EMPTY_MAP_FILTERS,
  buildingAges: ['super_new', 'modern'],
};

export const BEDROOM_OPTIONS = ['Studio', '1BR', '2BR', '3BR'];

export const AGE_OPTIONS: { label: string; value: string; test: (year?: number | null) => boolean }[] = [
  { label: 'New (2020+)', value: 'super_new', test: (y) => (y ?? 0) >= 2020 },
  { label: 'Modern (2000-19)', value: 'modern', test: (y) => (y ?? 0) >= 2000 && (y ?? 0) < 2020 },
  { label: 'Established (1980-99)', value: 'established', test: (y) => (y ?? 0) >= 1980 && (y ?? 0) < 2000 },
  { label: 'Vintage (pre-1980)', value: 'vintage', test: (y) => (y ?? 0) > 0 && (y ?? 0) < 1980 },
];

export const BUDGET_PRESETS = [
  { label: '$1.5k', value: 1500 },
  { label: '$2k', value: 2000 },
  { label: '$2.5k', value: 2500 },
  { label: '$3k', value: 3000 },
  { label: '$3.5k', value: 3500 },
];

// Pure filter function — client-side post-query narrowing of already-returned
// results (it never triggers a new search).
//
// P4 migration: uses normalizeApartment() from the canonical shape instead of
// defensively walking 5+ field variants per filter dimension. One normalizer,
// one shape, one place to fix when the DB schema evolves.
export function filterApartments<T>(apts: T[], filters: MapFilters): T[] {
  if (
    filters.minBudget == null &&
    filters.maxBudget == null &&
    filters.bedrooms.length === 0 &&
    filters.buildingAges.length === 0
  ) {
    return apts;
  }

  return apts.filter((apt) => {
    // Convert raw DB record → canonical shape. The normalizer handles
    // every known field variant (starting_prices, starting_prices_display,
    // price_ranges, floorplans, year_built/built_year, etc.) in one place.
    let canonical;
    try {
      canonical = normalizeApartment(apt as unknown as Record<string, unknown>);
    } catch {
      // If normalisation fails (e.g. missing `name`), keep the apartment
      // visible rather than silently dropping it.
      return true;
    }

    // Inventory and price are different evidence. A floorplan can confirm
    // a bedroom type without a published rent (including Firedev-only plans).
    const raw = apt as Record<string, unknown>;
    const inventory = new Set<string>(Object.keys(canonical.startingPrices));
    for (const source of [raw.floorplans, raw.firedev_floorplans]) {
      if (!Array.isArray(source)) continue;
      for (const plan of source) {
        if (!plan || typeof plan !== 'object') continue;
        const bed = normalizeBedKey(plan.bedrooms) || normalizeBedKey(plan.category);
        if (bed) inventory.add(bed);
      }
    }
    for (const source of [raw.bedrooms, raw.bedroom_types]) {
      if (!Array.isArray(source)) continue;
      for (const value of source) {
        const bed = normalizeBedKey(value);
        if (bed) inventory.add(bed);
      }
    }
    const wanted = filters.bedrooms.map(normalizeBedKey).filter(Boolean);
    if (wanted.length && inventory.size && !wanted.some(b => b && inventory.has(b))) return false;

    // Only the requested bedrooms can establish affordability. Unknown rent
    // remains visible, but never borrows a cheaper studio's price.
    const price = cheapestStartingPrice(canonical.startingPrices, filters.bedrooms)?.value;
    if (price != null) {
      if (filters.maxBudget != null && price > filters.maxBudget) return false;
      if (filters.minBudget != null && price < filters.minBudget) return false;
    }

    // Building age — canonical yearBuilt is already resolved from
    // year_built / built_year variants.
    if (filters.buildingAges.length > 0) {
      const year = canonical.yearBuilt ?? null;
      const matches = AGE_OPTIONS
        .filter((x) => filters.buildingAges.includes(x.value))
        .some((x) => x.test(year));
      if (!matches) return false;
    }

    return true;
  });
}

export function countActiveFilters(f: MapFilters): number {
  let n = 0;
  if (f.minBudget != null) n += 1;
  if (f.maxBudget != null) n += 1;
  n += f.bedrooms.length;
  n += f.buildingAges.length;
  return n;
}

/** Normalize a Criteria-panel bedroom label to the MapFilters bedroom shape. */
export function normalizeCriteriaBedroom(label: string): string | null {
  const l = label.toLowerCase();
  if (l.includes('studio')) return 'Studio';
  if (/^\s*1/.test(l) || l.includes('1 bedroom') || l.includes('1br')) return '1BR';
  if (/^\s*2/.test(l) || l.includes('2 bedroom') || l.includes('2br')) return '2BR';
  if (/^\s*3/.test(l) || /^\s*4/.test(l) || l.includes('3 bedroom') || l.includes('4 bedroom') || l.includes('3br')) return '3BR';
  return null;
}

// Criteria-panel building-age labels → MapFilters age values (AGE_OPTIONS
// vocabulary). The panel stores display labels; the map narrowing speaks
// super_new/modern/established/vintage. This table is the one place the two
// dialects meet. (The previous inline copy in performSearch mapped to
// 'New'/'Modern'/'Established'/'Older' — values AGE_OPTIONS never matched,
// so inherited age filters silently never narrowed anything.)
const CRITERIA_AGE_TO_MAP: Record<string, string[]> = {
  'New (Built 2020+)': ['super_new'],
  'Modern (Built 2010-2019)': ['modern'],
  'Established (Built 2000-2009)': ['modern'],
  // "Mature" = before 2000 → both map bands below 2000.
  'Mature (Built before 2000)': ['established', 'vintage'],
};

/** Project the canonical Criteria filter panel onto the MapFilters shape —
 * what a Criteria Search "means" when it carries over to map narrowing.
 * Used by inheritToLens to seed the Lens (Phase 8b §2.5 / ADR-0017). */
export function mapFiltersFromCriteria(filters: FiltersState): Partial<MapFilters> {
  const bedrooms = Array.from(new Set(
    (filters.bedrooms || [])
      .map((b: string) => normalizeCriteriaBedroom(b))
      .filter((b): b is string => !!b)
  ));
  // The panel historically stored display labels ('New (Built 2020+)') but
  // the wizard now stores AGE_OPTIONS ids ('super_new') directly — accept
  // both so neither surface's age selection silently drops on carry-over.
  const knownAgeValues = new Set(AGE_OPTIONS.map((o) => o.value));
  const buildingAges = Array.from(new Set(
    (filters.buildingAges || []).flatMap(
      (a: string) => CRITERIA_AGE_TO_MAP[a] || (knownAgeValues.has(a) ? [a] : [])
    )
  ));
  // minBudget deliberately NOT inherited: the Criteria wizard's default
  // floor ($1,500) would hide cheap gems on the map without the renter
  // ever asking for a floor.
  return {
    maxBudget: filters.maxBudget || null,
    bedrooms,
    buildingAges,
  };
}

/** Inverse of `mapFiltersFromCriteria`: project the map's active narrowing
 * back onto the Criteria wizard's FiltersState shape, so "Edit your search"
 * from the browse map opens the wizard already knowing what the map knows
 * (verified live 2026-09-03: the wizard previously opened with its own
 * defaults — $3,000, no bedrooms — while the map said 1BR under $3,600).
 *
 * The two surfaces share the bedrooms ('Studio'/'1BR'/…) and building-age
 * ('super_new'/…) vocabularies, so this is a straight carry: only values the
 * wizard actually offers pass through. minBudget is not carried — the wizard
 * has no floor control, and inventing one would hide cheap gems. */
export function criteriaFromMapFilters(map: MapFilters): Partial<FiltersState> {
  const wizardBeds = new Set(BEDROOM_OPTIONS);
  const wizardAges = new Set(AGE_OPTIONS.map((o) => o.value));
  const out: Partial<FiltersState> = {
    bedrooms: map.bedrooms.filter((b) => wizardBeds.has(b)),
    buildingAges: map.buildingAges.filter((a) => wizardAges.has(a)),
  };
  if (map.maxBudget != null) out.maxBudget = map.maxBudget;
  return out;
}
